import { formatDate } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { io } from 'socket.io-client';

import CookieService from '@/services/CookieService';
import { EAgentExecutionStatus, TAgentProperty, TSocketAgentErrorLog, TSocketAgentLog, isErrorLog } from '@/types/agents';
import { EUserTokenNames } from '@/types/user';
import { DEFAULT_AGENT_ERROR_MESSAGE } from '@/constants/global';
import { EAgentService } from '@/types/agents';

const accessToken = CookieService.getCookie(EUserTokenNames.accessToken);

const socket = accessToken ? io(`${process.env.NEXT_PUBLIC_SOCKET_API_URL}/agent`, {
  extraHeaders: {
    auth: accessToken,
  },
}) : null;

interface IUseExecutionLogsPageProps {
  isExecuting?: boolean;
  agentId?: string;
  agentConfigId?: string;
  setIsExecuting?: (isExecuting: boolean) => void;
  properties?: TAgentProperty[];
  agentService?: EAgentService;
}

const formatTime = (dateString?: string) => {
  return dateString ? formatDate(dateString, 'h:mm:ss') : '--:--:--';
};

const calculateDuration = (startDate: string, endDate: string) => {
  if (!startDate || !endDate) {return '--';}
  const duration = (new Date(endDate).getTime() -
                  new Date(startDate).getTime()) / 1000;

  return `${duration}s`;
};

const useExecutionLogsPage = ({ isExecuting, agentId, agentConfigId, setIsExecuting, properties, agentService }: IUseExecutionLogsPageProps) => {
  const [socketLogs, setSocketLogs] = useState<(TSocketAgentLog | TSocketAgentErrorLog)[]>([]);
  const [isLoadingRunAgent, setIsLoadingRunAgent] = useState(false);

  const handleBackButtonClick = useCallback(() => {
    setIsExecuting?.(false);
  }, [setIsExecuting]);

  const handleRunAgent = useCallback(() => {
    if (!agentId) {
      return;
    }

    setIsLoadingRunAgent(true);
    setSocketLogs([]);
    const shouldSendConfigId = agentService !== EAgentService.NATIVE || agentConfigId;
    const payload: { agentRunConfigId?: string; properties: TAgentProperty[] } = {
      properties: properties || [],
      ...(shouldSendConfigId ? { agentRunConfigId: agentConfigId } : {}),
    };

    socket?.emit('run-agent', payload);
  }, [agentId, agentConfigId, properties, agentService]);

  const handleRetryClick = useCallback(() => {
    if (!agentId) {
      return;
    }

    setSocketLogs([]);
    handleRunAgent();
  }, [agentId, handleRunAgent]);

  const handleSocketLog = useCallback((data: TSocketAgentLog) => {
    setSocketLogs(prev => {
      const newLogs = [...prev, data];
      if (data.finishedAt || data.status === EAgentExecutionStatus.SUCCESS || data.status === EAgentExecutionStatus.FAILED) {
        setIsLoadingRunAgent(false);
      }

      return newLogs;
    });
  }, []);

  type SocketErrorPayload = { code?: number; message?: string };

  const handleSocketError = useCallback((data: SocketErrorPayload) => {
    setSocketLogs(prev => [...prev, {
      code: data?.code ?? 500,
      message: data?.message ?? 'An error occurred',
      createdAt: new Date().toISOString(),
    }]);
    setIsLoadingRunAgent(false);
  }, []);

  const normalizeErrorPayload = useCallback((data: unknown): SocketErrorPayload => {
    if (typeof data === 'object' && data !== null) {
      return data as SocketErrorPayload;
    }

    return {};
  }, []);

  useEffect(() => {
    socket?.on('connection', () => {
      console.log('connected');
    });

    socket?.on('error', (data) => {
      handleSocketError(normalizeErrorPayload(data));
    });

    socket?.on('run-agent', handleSocketLog);
    socket?.on('running', handleSocketLog);

    if (agentId && isExecuting) {
      handleRunAgent();
    }

    return () => {
      socket?.off('connection');
      socket?.off('error');
      socket?.off('run-agent');
      socket?.off('running');
    };
  }, [agentId, isExecuting, handleRunAgent, handleSocketLog, handleSocketError]);

  const logs = useMemo(() => {
    if (!socketLogs.length) {
      return [];
    }

    const runAgentLogs = socketLogs.filter((l): l is TSocketAgentLog => !isErrorLog(l));
    const errorLogs = socketLogs.filter(isErrorLog);
    const start = runAgentLogs[0];
    const end = runAgentLogs[runAgentLogs.length - 1];
    const firstLog = socketLogs[0];
    const firstTime = 'createdAt' in firstLog ? firstLog.createdAt : undefined;

    const logItems: Array<{
      title: string;
      time: string;
      status?: EAgentExecutionStatus;
      duration?: string;
      error?: string;
      errorCode?: number;
    }> = [
      { title: 'Input sent', time: formatTime(firstTime) },
      { title: 'Agent Started', time: formatTime(firstTime) },
      ...errorLogs.map((e) => ({
        title: 'Error',
        time: formatTime(e.createdAt),
        status: EAgentExecutionStatus.FAILED,
        error: e.message,
        errorCode: e.code,
      })),
    ];

    if (end) {
      const errorMessage =
        end.status === EAgentExecutionStatus.FAILED && !end.error
          ? DEFAULT_AGENT_ERROR_MESSAGE
          : end.error;

      logItems.push({
        title: 'Agent Output Received',
        time: formatTime(end?.finishedAt),
        status: end?.status ?? EAgentExecutionStatus.IN_PROGRESS,
        error: errorMessage,
        duration: start && end?.finishedAt ? calculateDuration(start.createdAt, end.finishedAt) : '--',
      });
    }

    return logItems;
  }, [socketLogs]);

  return {
    logs,
    socketLogs,
    isLoadingRunAgent,
    handleBackButtonClick,
    handleRetryClick,
  };
};

export default useExecutionLogsPage;
